import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Search } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Input } from "~/components/ui/input";
import { RadioGroup, RadioGroupIndicator } from "~/components/ui/radio-group";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { stripMarkdownToText } from "~/lib/stripMarkdownToText";
import { cn } from "~/lib/utils";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { ProjectIcon } from "~/pages/manifold-nextgen/components/projects/ProjectIcon";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import {
  ProjectList,
  ProjectListItem,
  ProjectListView,
  mutateProjectList,
  projectListPath,
} from "~/services/projects";

interface AddToProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Names the entity in the dialog's copy and its toasts. */
  entityName: string;
  /** Whether this project already holds the entity. */
  isAttached: (project: ProjectListItem) => boolean;
  /** Attaches to the chosen project and revalidates the entity's own keys; the
   *  projects list is this dialog's to revalidate. */
  attach: (projectId: string) => Promise<unknown>;
}

/**
 * Picks one project to attach an agent or a dataset to.
 *
 * The list is `view=owned` — exactly the set the backend's `can_manage_agents`
 * / `can_manage_datasets` guards accept, so the picker cannot offer a project
 * the attach would reject. Fetched only while open: resolving the caller's
 * project grants is too costly for every overview page load.
 */
export const AddToProjectDialog = ({
  open,
  onOpenChange,
  entityName,
  isAttached,
  attach,
}: AddToProjectDialogProps) => {
  const { data, error, isLoading } = useFetcher<ProjectList>(
    open ? projectListPath({ view: ProjectListView.OWNED }) : null
  );
  const { showSuccess } = useToast();

  const [selected, setSelected] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      setSelected("");
      setSearch("");
    }
  }, [open]);

  // Most recent activity first, matching the projects list's own default order.
  const projects = useMemo(
    () =>
      [...(data?.items ?? [])].sort(
        (a, b) => Date.parse(b.last_activity_at) - Date.parse(a.last_activity_at)
      ),
    [data?.items]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return projects;
    }
    return projects.filter(
      (p) => p.name.toLowerCase().includes(q) || (p.description ?? "").toLowerCase().includes(q)
    );
  }, [projects, search]);

  const { execute, isPending } = useAsyncMutation((projectId: string) => attach(projectId), {
    errorMessage: `Failed to add "${entityName}" to project`,
    onSuccess: async () => {
      await mutateProjectList();
      const project = projects.find((p) => p.id === selected);
      showSuccess(`"${entityName}" added to "${project?.name ?? "project"}"`);
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add to project"
      description={`Choose a project to add "${entityName}" to.`}
      contentClassName="sm:max-w-[640px]"
      onPrimary={() => void execute(selected)}
      isPending={isPending}
      primaryLabel="Add"
      primaryPendingLabel="Adding…"
      primaryDisabled={!selected}
    >
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search projects..."
          aria-label="Search projects"
          className="pl-9"
        />
      </div>

      {/* Grows to the list, up to half the viewport. The floor keeps the
          dialog from resizing on every keystroke as the search narrows. */}
      <div className="-mx-2 flex max-h-[50vh] min-h-48 flex-col overflow-y-auto">
        {isLoading && (
          <div role="status" className="flex flex-col gap-2 px-2 py-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 px-2 py-8 text-center text-destructive"
          >
            <AlertCircle className="size-6" aria-hidden="true" />
            <p className="text-sm font-medium">Failed to load projects</p>
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <p className="px-2 py-8 text-center text-sm text-muted-foreground">
            {search
              ? "No projects match your search."
              : "You don't manage any projects yet. Create one to add things to it."}
          </p>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <RadioGroup value={selected} onValueChange={setSelected} className="gap-0">
            {filtered.map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                attached={isAttached(project)}
                selected={selected === project.id}
              />
            ))}
          </RadioGroup>
        )}
      </div>
    </FormDialog>
  );
};

interface ProjectRowProps {
  project: ProjectListItem;
  attached: boolean;
  selected: boolean;
}

const ProjectRow = ({ project, attached, selected }: ProjectRowProps) => {
  const radioId = `project-row-${project.id}`;
  const description = useMemo(
    () => (project.description ? stripMarkdownToText(project.description) : ""),
    [project.description]
  );
  return (
    <label
      htmlFor={radioId}
      className={cn(
        // min-w-0: the row is a grid item, so without it the row is sized to
        // the description's full width and the whole list scrolls sideways.
        "flex min-w-0 items-center gap-3 rounded-md px-2 py-2 transition-colors",
        attached ? "cursor-default opacity-60" : "cursor-pointer hover:bg-accent/40",
        selected && "bg-accent/60"
      )}
    >
      <ProjectIcon project={project} size={20} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{project.name}</p>
        <p className="truncate text-xs text-muted-foreground">{description || "No description"}</p>
      </div>
      {attached && <Badge variant="secondary">Added</Badge>}
      <RadioGroupIndicator
        id={radioId}
        value={project.id}
        disabled={attached}
        aria-label={project.name}
      />
    </label>
  );
};
